import { useEffect, useState, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Trophy, Upload, ImageOff, Loader2 } from "lucide-react";
import { adminFetch, adminUpload, photoSrc } from "./adminApi";

const CATEGORIES = [
  { id: "banking", label: "Banking" },
  { id: "ssc", label: "SSC" },
  { id: "railway", label: "Railway" },
  { id: "other", label: "Other Govt" },
];

const EMPTY = { name: "", role: "", organization: "", category: "banking", photo: "", order: "", is_active: true };

const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar = ({ name, photo, size = "h-11 w-11", text = "text-sm" }) => {
  const src = photoSrc(photo);
  if (src)
    return <img src={src} alt={name} className={`${size} rounded-lg object-cover object-top bg-slate-100`} loading="lazy" />;
  return (
    <div className={`${size} rounded-lg bg-gradient-to-br from-vedanti-navy to-blue-600 text-white font-bold ${text} flex items-center justify-center`}>
      {initials(name)}
    </div>
  );
};

const Input = ({ label, ...props }) => (
  <div>
    <label className="block text-xs font-semibold text-vedanti-navy mb-1">{label}</label>
    <input
      {...props}
      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40 text-sm"
    />
  </div>
);

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await adminFetch("/admin/achievers"));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (a) => { setForm({ ...EMPTY, ...a, order: a.order ?? "" }); setEditing(a); };
  const close = () => setEditing(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await adminUpload(file);
      setForm((f) => ({ ...f, photo: url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name || !form.role || !form.organization) {
      toast.error("Name, Role and Organization are required");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      role: form.role,
      organization: form.organization,
      category: form.category,
      photo: form.photo || null,
      order: form.order === "" ? null : Number(form.order),
      is_active: form.is_active,
    };
    try {
      if (editing.id) {
        await adminFetch(`/admin/achievers/${editing.id}`, { method: "PUT", body: JSON.stringify(payload) });
        toast.success("Achiever updated");
      } else {
        await adminFetch("/admin/achievers", { method: "POST", body: JSON.stringify(payload) });
        toast.success("Achiever added");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"? This cannot be undone.`)) return;
    try {
      await adminFetch(`/admin/achievers/${a.id}`, { method: "DELETE" });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="admin-gallery-page">
      <Helmet><title>Manage Gallery | Vedanti Admin</title></Helmet>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-vedanti-navy flex items-center gap-2">
            <Trophy size={22} className="text-vedanti-orange" /> Achievers Gallery
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">{items.length} students · add names, selections &amp; optional photos</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-vedanti-orange hover:bg-orange-600 text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-all" data-testid="add-achiever-btn">
          <Plus size={16} /> Add Achiever
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-slate-400">Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Student</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Role</th>
                <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Category</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50" data-testid={`admin-achiever-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={a.name} photo={a.photo} />
                      <div>
                        <p className="font-semibold text-vedanti-navy leading-tight">{a.name}</p>
                        <p className="text-[11px] text-slate-400">{a.organization}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-600 font-medium">{a.role}</td>
                  <td className="px-4 py-3 hidden lg:table-cell uppercase text-xs font-bold text-slate-500">{a.category}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${a.is_active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
                      {a.is_active ? "Visible" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(a)} className="text-slate-500 hover:text-vedanti-navy p-1.5" data-testid={`edit-achiever-${a.id}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(a)} className="text-slate-400 hover:text-red-500 p-1.5" data-testid={`delete-achiever-${a.id}`}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">No achievers yet. Click “Add Achiever”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer form */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="achiever-form-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-heading font-bold text-vedanti-navy">{editing.id ? "Edit" : "New"} Achiever</h2>
              <button onClick={close} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Photo uploader */}
              <div>
                <label className="block text-xs font-semibold text-vedanti-navy mb-2">Photo <span className="text-slate-400 font-normal">(optional)</span></label>
                <div className="flex items-center gap-4">
                  <Avatar name={form.name} photo={form.photo} size="h-20 w-20" text="text-xl" />
                  <div className="flex flex-col gap-2">
                    <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="achiever-photo-input" />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-vedanti-navy border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50 disabled:opacity-60"
                      data-testid="achiever-upload-btn"
                    >
                      {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {uploading ? "Uploading…" : form.photo ? "Change Photo" : "Upload Photo"}
                    </button>
                    {form.photo && (
                      <button type="button" onClick={() => setForm((f) => ({ ...f, photo: "" }))} className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-600" data-testid="achiever-remove-photo">
                        <ImageOff size={13} /> Remove photo
                      </button>
                    )}
                    <p className="text-[11px] text-slate-400">JPG/PNG/WebP · max 5MB. No photo → initials shown.</p>
                  </div>
                </div>
              </div>

              <Input label="Student Name *" value={form.name} onChange={set("name")} data-testid="achiever-field-name" placeholder="e.g. Kiran Jadhav" />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Role / Selection *" value={form.role} onChange={set("role")} data-testid="achiever-field-role" placeholder="e.g. IBPS Clerk" />
                <div>
                  <label className="block text-xs font-semibold text-vedanti-navy mb-1">Category</label>
                  <select value={form.category} onChange={set("category")} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-vedanti-orange/40" data-testid="achiever-field-category">
                    {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <Input label="Organization *" value={form.organization} onChange={set("organization")} data-testid="achiever-field-org" placeholder="e.g. State Bank of India" />
              <Input label="Display Order" type="number" value={form.order} onChange={set("order")} placeholder="Lower shows first (auto if blank)" data-testid="achiever-field-order" />
              <label className="flex items-center gap-2 text-sm font-semibold text-vedanti-navy">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-vedanti-orange" data-testid="achiever-field-active" />
                Show on public gallery
              </label>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex justify-end gap-3">
              <button onClick={close} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
              <button onClick={save} disabled={saving || uploading} className="px-5 py-2 rounded-lg text-sm font-bold bg-vedanti-orange hover:bg-orange-600 text-white disabled:opacity-60" data-testid="achiever-save-btn">
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
